import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {api} from '@/api/client'
import {endpoints} from '@/api/endpoints'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Pagination} from '@/components/ui/pagination'
import {ErrorBanner} from '@/components/ui/error-banner'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {DatePicker} from '@/components/shared/DatePicker'
import {DetailField} from '@/components/shared/DetailField'
import {DetailList} from '@/components/shared/DetailList'
import {EmptyState} from '@/components/shared/EmptyState'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {PageLayout} from '@/components/shared/PageLayout'
import {buildFilterQuery} from '@/lib/build-filter-query'
import {CONCEPT_ICON} from '@/lib/concepts'
import {formatTimestamp} from '@/lib/date-utils'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {PageRequestFields} from '@/types/page'
import {ScriptExecuteLogControllerListEndpoint} from '@/types/endpoint-contracts'
import {
    ScriptExecuteLogMode,
    type ScriptExecuteLog,
    type ScriptExecuteLogPage,
} from '@/types/script-execute-log'

const ALL = '__all__'
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

interface Filters {
    actorUsername: string
    mode: '' | ScriptExecuteLogMode
    successFilter: '' | 'true' | 'false'
    occurredAtFrom: string
    occurredAtTo: string
}

const EMPTY_FILTERS: Filters = {
    actorUsername: '',
    mode: '',
    successFilter: '',
    occurredAtFrom: '',
    occurredAtTo: '',
}

export function ScriptExecuteLogPage() {
    const i18n = useI18n()
    usePageTitle(i18n.scriptExecuteLog.pageTitle)

    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(50)
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
    const [selectedRow, setSelectedRow] = useState<ScriptExecuteLog | null>(null)

    const queryString = useMemo(() => {
        /* eslint-disable i18next/no-literal-string -- technical URL query construction, not user-visible text */
        const pagePart = [
            `${PageRequestFields.page}=${page}`,
            `${PageRequestFields.size}=${pageSize}`,
            `${PageRequestFields.sort}=${ScriptExecuteLogControllerListEndpoint.sort.OCCURRED_AT},desc`,
        ].join('&')
        /* eslint-enable i18next/no-literal-string */
        const filterPart = buildFilterQuery(ScriptExecuteLogControllerListEndpoint, {
            ACTOR_USERNAME: filters.actorUsername || undefined,
            MODE: filters.mode || undefined,
            SUCCESS: filters.successFilter || undefined,
            OCCURRED_AT_FROM: filters.occurredAtFrom || undefined,
            OCCURRED_AT_TO: filters.occurredAtTo || undefined,
        })
        return filterPart ? `${pagePart}&${filterPart}` : pagePart
    }, [page, pageSize, filters])

    const {data, isLoading, error} = useQuery<ScriptExecuteLogPage>({
        queryKey: ['script-execute-log', queryString],
        queryFn: () => api.get<ScriptExecuteLogPage>(
            `${endpoints.scriptExecuteLog.list()}?${queryString}`,
        ),
    })

    const totalItems = data?.totalElements ?? 0
    const totalPages = data?.totalPages ?? 0
    const hasFilters = useMemo(() =>
        filters.actorUsername.length > 0 || filters.mode !== '' || filters.successFilter !== ''
        || filters.occurredAtFrom.length > 0 || filters.occurredAtTo.length > 0,
        [filters])

    // WHY: HQG follow-up 766 §7 — filtering is server-side now (no client-side
    // useMemo .filter pass over actorUsername). Resetting page to 0 on every
    // filter change avoids leaving the user stranded on an empty page index.
    const rows = data?.content ?? []
    const updateFilters = (next: Filters) => {
        setFilters(next)
        setPage(0)
    }

    if (error) {
        return <ErrorBanner error={error as Error}/>
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.scriptExecuteLog}
            title={i18n.scriptExecuteLog.title}
            description={i18n.scriptExecuteLog.description}
            count={totalItems}
            data-testid="script-execute-log-page"
            data-help-prefix="scriptExecuteLog"
            filters={
                <InlineRow gap="sm" wrap data-testid="script-execute-log-filters">
                    <Input
                        type="text"
                        placeholder={i18n.scriptExecuteLog.filters.actorPlaceholder as string}
                        value={filters.actorUsername}
                        onChange={(e) => updateFilters({...filters, actorUsername: e.target.value})}
                        aria-label={i18n.scriptExecuteLog.filters.actor as string}
                        data-testid="filter-actor"
                    />
                    <Select
                        value={filters.mode === '' ? ALL : filters.mode}
                        onValueChange={(v) => updateFilters({...filters, mode: v === ALL ? '' : (v as ScriptExecuteLogMode)})}
                    >
                        <SelectTrigger data-testid="filter-mode" aria-label={i18n.scriptExecuteLog.filters.mode as string}>
                            <SelectValue placeholder={i18n.scriptExecuteLog.filters.modeAll}/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>{i18n.scriptExecuteLog.filters.modeAll}</SelectItem>
                            <SelectItem value={ScriptExecuteLogMode.preview}>
                                {i18n.scriptExecuteLog.filters.modePreview}
                            </SelectItem>
                            <SelectItem value={ScriptExecuteLogMode.runtime}>
                                {i18n.scriptExecuteLog.filters.modeRuntime}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={filters.successFilter === '' ? ALL : filters.successFilter}
                        onValueChange={(v) => updateFilters({...filters, successFilter: v === ALL ? '' : (v as 'true' | 'false')})}
                    >
                        <SelectTrigger data-testid="filter-success" aria-label={i18n.scriptExecuteLog.filters.outcome as string}>
                            <SelectValue placeholder={i18n.scriptExecuteLog.filters.outcomeAll}/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>{i18n.scriptExecuteLog.filters.outcomeAll}</SelectItem>
                            <SelectItem value={'true'}>{i18n.scriptExecuteLog.filters.outcomeSuccess}</SelectItem>
                            <SelectItem value={'false'}>{i18n.scriptExecuteLog.filters.outcomeFailure}</SelectItem>
                        </SelectContent>
                    </Select>
                    <DatePicker
                        withTime
                        value={filters.occurredAtFrom}
                        onChange={(v) => updateFilters({...filters, occurredAtFrom: v})}
                        ariaLabel={i18n.scriptExecuteLog.filters.occurredFrom as string}
                        placeholder={i18n.scriptExecuteLog.filters.occurredFrom as string}
                        testId="filter-occurred-from"
                    />
                    <DatePicker
                        withTime
                        value={filters.occurredAtTo}
                        onChange={(v) => updateFilters({...filters, occurredAtTo: v})}
                        ariaLabel={i18n.scriptExecuteLog.filters.occurredTo as string}
                        placeholder={i18n.scriptExecuteLog.filters.occurredTo as string}
                        testId="filter-occurred-to"
                    />
                    {hasFilters && (
                        <Button
                            variant="ghost"
                            onClick={() => updateFilters(EMPTY_FILTERS)}
                            data-testid="clear-filters"
                        >
                            {i18n.scriptExecuteLog.filters.clear}
                        </Button>
                    )}
                </InlineRow>
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-occurred-at" data-help-key="occurredAt">
                            {i18n.scriptExecuteLog.columns.occurredAt}
                        </TableHead>
                        <TableHead data-testid="col-mode" data-help-key="mode">
                            {i18n.scriptExecuteLog.columns.mode}
                        </TableHead>
                        <TableHead data-testid="col-actor" data-help-key="actor">
                            {i18n.scriptExecuteLog.columns.actor}
                        </TableHead>
                        <TableHead data-testid="col-sql-text" data-help-key="sqlText">
                            {i18n.scriptExecuteLog.columns.sqlText}
                        </TableHead>
                        <TableHead data-testid="col-duration-ms" data-help-key="durationMs">
                            {i18n.scriptExecuteLog.columns.durationMs}
                        </TableHead>
                        <TableHead data-testid="col-success" data-help-key="success">
                            {i18n.scriptExecuteLog.columns.success}
                        </TableHead>
                        <TableHead data-testid="col-error-code" data-help-key="errorCode">
                            {i18n.scriptExecuteLog.columns.errorCode}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={7}>
                                <MutedText>{i18n.common.loading}</MutedText>
                            </TableCell>
                        </TableRow>
                    ) : rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={7}>
                                <EmptyState
                                    icon={CONCEPT_ICON.scriptExecuteLog}
                                    title={hasFilters
                                        ? i18n.scriptExecuteLog.emptyMatchMessage as string
                                        : i18n.scriptExecuteLog.emptyMessage as string}
                                />
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row) => (
                            <Row key={row.id} row={row} onOpen={() => setSelectedRow(row)}/>
                        ))
                    )}
                </TableBody>
            </Table>
            <DetailDialog row={selectedRow} onClose={() => setSelectedRow(null)}/>
        </PageLayout>
    )
}

function Row({row, onOpen}: {row: ScriptExecuteLog; onOpen: () => void}) {
    const i18n = useI18n()
    const sqlPreview = row.sqlText.length > 80 ? row.sqlText.slice(0, 80) + '…' : row.sqlText
    return (
        <ClickableTableRow onClick={onOpen} data-testid={`script-execute-log-row-${row.id}`}>
            <TableCell>{formatTimestamp(row.occurredAt)}</TableCell>
            <TableCell>
                {row.mode === ScriptExecuteLogMode.preview ? (
                    <Badge variant={BadgeVariant.OUTLINE} data-testid="mode-preview">
                        {i18n.scriptExecuteLog.badges.preview}
                    </Badge>
                ) : (
                    <Badge variant={BadgeVariant.SECONDARY} data-testid="mode-runtime">
                        {i18n.scriptExecuteLog.badges.runtime}
                    </Badge>
                )}
            </TableCell>
            <TableCell>{row.actorUsername ?? '—'}</TableCell>
            <TableCell title={row.sqlText} data-testid={`sql-text-${row.id}`}>
                <code>{sqlPreview}</code>
            </TableCell>
            <TableCell>{row.durationMs ?? '—'}</TableCell>
            <TableCell>
                {row.success ? (
                    <Badge variant={BadgeVariant.SUCCESS} data-testid="success-true">
                        {i18n.scriptExecuteLog.badges.success}
                    </Badge>
                ) : (
                    <Badge variant={BadgeVariant.DESTRUCTIVE} data-testid="success-false">
                        {i18n.scriptExecuteLog.badges.failure}
                    </Badge>
                )}
            </TableCell>
            <TableCell>{row.errorCode ?? '—'}</TableCell>
        </ClickableTableRow>
    )
}

function DetailDialog({row, onClose}: {row: ScriptExecuteLog | null; onClose: () => void}) {
    const i18n = useI18n()
    return (
        <Dialog open={row !== null} onOpenChange={(open) => !open && onClose()}>
            <DialogContent data-testid="script-execute-log-detail">
                <DialogHeader>
                    <DialogTitle>{i18n.scriptExecuteLog.detail.title}</DialogTitle>
                </DialogHeader>
                {row !== null && (
                    <DetailList columns={2} gap="md">
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.occurredAt}
                            value={formatTimestamp(row.occurredAt)}
                        />
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.mode}
                            value={
                                row.mode === ScriptExecuteLogMode.preview ? (
                                    <Badge variant={BadgeVariant.OUTLINE}>
                                        {i18n.scriptExecuteLog.badges.preview}
                                    </Badge>
                                ) : (
                                    <Badge variant={BadgeVariant.SECONDARY}>
                                        {i18n.scriptExecuteLog.badges.runtime}
                                    </Badge>
                                )
                            }
                        />
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.actor}
                            value={row.actorUsername ?? '—'}
                        />
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.outcome}
                            value={
                                row.success ? (
                                    <Badge variant={BadgeVariant.SUCCESS}>
                                        {i18n.scriptExecuteLog.badges.success}
                                    </Badge>
                                ) : (
                                    <Badge variant={BadgeVariant.DESTRUCTIVE}>
                                        {i18n.scriptExecuteLog.badges.failure}
                                    </Badge>
                                )
                            }
                        />
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.duration}
                            value={row.durationMs ?? '—'}
                        />
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.actorUserId}
                            value={<MutedText size="sm">{row.actorUserId ?? '—'}</MutedText>}
                        />
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.jobExecutionId}
                            value={<MutedText size="sm">{row.jobExecutionId ?? '—'}</MutedText>}
                            fullWidth
                        />
                        <DetailField
                            label={i18n.scriptExecuteLog.detail.sqlText}
                            value={
                                <CodeBlock maxHeight="md" data-testid="detail-sql-text">
                                    {row.sqlText}
                                </CodeBlock>
                            }
                            fullWidth
                        />
                        {!row.success && (
                            <>
                                <DetailField
                                    label={i18n.scriptExecuteLog.detail.errorCode}
                                    value={row.errorCode ?? '—'}
                                    fullWidth
                                />
                                <DetailField
                                    label={i18n.scriptExecuteLog.detail.errorMessage}
                                    value={
                                        <CodeBlock variant="error" maxHeight="md" data-testid="detail-error-message">
                                            {row.errorMessage ?? '—'}
                                        </CodeBlock>
                                    }
                                    fullWidth
                                />
                            </>
                        )}
                    </DetailList>
                )}
                <InlineRow justify="end">
                    <Button variant="outline" onClick={onClose} data-testid="detail-close">
                        {i18n.scriptExecuteLog.detail.close}
                    </Button>
                </InlineRow>
            </DialogContent>
        </Dialog>
    )
}
